/* Cinemax landing page — scroll-driven animations, before/after comparator,
   camera selector. Single IIFE, no dependency. Re-initializes on Shopify
   theme-editor section load/unload events. */
(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

  var registry = {
    hero: [],
    convergence: [],
    reveals: [],
    beforeAfter: [],
    selectors: [],
    stickyDock: [],
    reviews: []
  };

  var raf = null;
  var scrollBound = false;

  function clamp01(n) {
    return Math.max(0, Math.min(1, n));
  }
  function clampN(v, a, b) {
    return Math.max(a, Math.min(b, v));
  }
  function lerp(a, b, t) {
    return a + (b - a) * t;
  }
  function seg(x, a, b) {
    return clampN((x - a) / (b - a), 0, 1);
  }

  /* ---------------- Hero: canvas frame-sequence scrub ----------------
     240 WebP frames (real alpha), drawn on <canvas> and scrubbed by scroll
     progress (hp, 0-1). A self-suspending rAF chain smooths hp toward its
     scroll-derived target and only keeps ticking while it hasn't settled,
     so there's no permanent 60fps loop when the page is idle. */
  // 1 = exact cover (fills the stage box on both axes, cropping whichever
  // side overflows). Below 1, the frame is drawn smaller than that, so a
  // thin margin of empty canvas can reappear at the edges — a deliberate
  // trade for a less aggressive crop.
  var HERO_COVER_ZOOM = 0.8;

  function computeHeroProgress(entry) {
    var rect = entry.track.getBoundingClientRect();
    // entry.stage's own measured height, not window.innerHeight: on mobile,
    // the two disagree the moment the browser chrome (address bar)
    // shows/hides mid-scroll — innerHeight is the dynamic "large viewport"
    // figure, while the stage is styled with the stable 100svh, so reading
    // its actual rendered height keeps this math self-consistent with what
    // CSS is doing instead of racing against a value that can jump
    // mid-gesture (that mismatch is what read as the page "rolling back").
    var total = rect.height - entry.stage.getBoundingClientRect().height;
    return clamp01(total > 0 ? (-rect.top) / total : 0);
  }

  function resizeHero(entry) {
    var rect = entry.canvas.getBoundingClientRect();
    entry.dpr = Math.min(window.devicePixelRatio || 1, 2);
    entry.W = rect.width;
    entry.H = rect.height;
    entry.canvas.width = Math.round(entry.W * entry.dpr);
    entry.canvas.height = Math.round(entry.H * entry.dpr);
    entry.ctx.setTransform(entry.dpr, 0, 0, entry.dpr, 0, 0);
    // Resizing the backing store resets the whole 2D context state per
    // spec, including smoothing — must be re-applied every time.
    entry.ctx.imageSmoothingEnabled = true;
    entry.ctx.imageSmoothingQuality = 'high';
  }

  function drawHeroFrame(entry) {
    var ctx = entry.ctx;
    ctx.clearRect(0, 0, entry.W, entry.H);
    if (!entry.total) return;
    var fi = Math.round(seg(entry.hp, 0, 0.92) * (entry.total - 1));
    var img = entry.frames[clampN(fi, 0, entry.total - 1)];
    if (img && img.complete && img.naturalWidth) {
      // Cover-fit, not fit-width: the stage box is already full viewport
      // height (100svh), but the source frames are wide/landscape — fitting
      // width alone left the frame short of that height, letterboxed with
      // empty canvas above/below. Filling whichever dimension needs more
      // scale (then cropping the overflow on the other) makes the frame
      // actually reach edge to edge, which is what makes it read as "zoomed
      // in" and cover more of a tall phone screen.
      var w = entry.W, h = w / entry.ratio;
      if (h < entry.H) { h = entry.H; w = h * entry.ratio; }
      w *= HERO_COVER_ZOOM; h *= HERO_COVER_ZOOM;
      ctx.save();
      ctx.globalAlpha = seg(entry.hp, 0, 0.05);
      ctx.drawImage(img, (entry.W - w) / 2, (entry.H - h) / 2, w, h);
      ctx.restore();
    }
  }

  function updateHeroHUD(entry) {
    if (prefersReducedMotion) return;
    var hp = entry.hp;
    // Visible at rest, fades OUT as hp increases (fully readable hero even
    // if JS never runs — the opposite curve keeps the h1/CTA hidden until
    // scrolled, which would fail a no-JS/slow-JS visitor).
    var fade = 1 - seg(hp, 0.04, 0.42);
    var rise = seg(hp, 0.04, 0.42);
    if (entry.wordmark) {
      entry.wordmark.style.opacity = fade.toFixed(3);
      var ls = lerp(0.14, 0.45, rise);
      entry.wordmark.style.letterSpacing = ls + 'em';
      entry.wordmark.style.transform = 'translateY(' + lerp(0, -16, rise).toFixed(1) + 'px)';
    }
    if (entry.tagline) entry.tagline.style.opacity = fade.toFixed(3);
    if (entry.ctaWrap) entry.ctaWrap.style.opacity = fade.toFixed(3);
    if (entry.hint) entry.hint.style.opacity = hp < 0.05 ? '1' : '0';
  }

  function tickHero(entry) {
    var SMOOTH = prefersReducedMotion ? 1 : 0.12;
    entry.hp += (entry.targetHp - entry.hp) * SMOOTH;
    var settled = Math.abs(entry.targetHp - entry.hp) < 0.0003;
    if (settled) entry.hp = entry.targetHp;
    entry.stage.style.setProperty('--hp', entry.hp.toFixed(4));
    drawHeroFrame(entry);
    updateHeroHUD(entry);
    if (!settled) {
      entry.ticking = true;
      requestAnimationFrame(function () { tickHero(entry); });
    } else {
      entry.ticking = false;
    }
  }

  function pokeHero(entry) {
    if (!entry.ready) return;
    entry.targetHp = computeHeroProgress(entry);
    if (!entry.ticking) tickHero(entry);
  }

  function initHero(section) {
    var track = section.querySelector('[data-cx-hero-track]');
    var stage = section.querySelector('[data-cx-hero-stage]');
    var canvas = section.querySelector('[data-cx-hero-canvas]');
    if (!track || !stage || !canvas) return;

    var dataEl = section.querySelector('[data-cx-hero-frames]');
    var frameUrls = [];
    try { frameUrls = JSON.parse(dataEl ? dataEl.textContent : '[]'); } catch (e) { frameUrls = []; }

    var entry = {
      track: track, stage: stage, canvas: canvas, ctx: canvas.getContext('2d'),
      wordmark: section.querySelector('[data-cx-hero-wordmark]'),
      tagline: section.querySelector('[data-cx-hero-tagline]'),
      ctaWrap: section.querySelector('[data-cx-hero-cta-wrap]'),
      hint: section.querySelector('[data-cx-hero-hint]'),
      loaderEl: section.querySelector('[data-cx-loader]'),
      loaderFill: section.querySelector('[data-cx-loader-fill]'),
      frames: new Array(frameUrls.length), total: frameUrls.length, loaded: 0,
      hp: 0, targetHp: 0, W: 0, H: 0, dpr: 1, ratio: 1600 / 900,
      ticking: false, ready: false
    };

    function becomeReady() {
      if (entry.ready) return;
      entry.ready = true;
      if (entry.loaderEl) entry.loaderEl.classList.add('is-hidden');
      resizeHero(entry);
      entry.targetHp = computeHeroProgress(entry);
      entry.hp = entry.targetHp;
      tickHero(entry);
    }

    function onFrameSettled() {
      entry.loaded++;
      if (entry.loaderFill) entry.loaderFill.style.width = (entry.loaded / entry.total * 100) + '%';
      if (entry.loaded === entry.total) becomeReady();
    }

    if (entry.total) {
      frameUrls.forEach(function (url, i) {
        var img = new Image();
        img.onload = onFrameSettled;
        img.onerror = onFrameSettled; // a broken frame must not hang the loader
        img.src = url;
        entry.frames[i] = img;
      });
      // Safety net: never let the loader block the page forever.
      setTimeout(becomeReady, 4000);
    } else {
      becomeReady();
    }

    registry.hero.push(entry);

    if (prefersReducedMotion) {
      stage.style.setProperty('--hp', '0');
      if (entry.wordmark) entry.wordmark.style.opacity = '1';
      if (entry.tagline) entry.tagline.style.opacity = '1';
      if (entry.ctaWrap) entry.ctaWrap.style.opacity = '1';
    }
  }

  /* ==========================================================================
     CONVERGENCE — battery turntable canvases (96 WebP frames each, real
     alpha), position/rotation/scale stay on the CSS --p transform calc()s;
     this only supplies the drawn content, same split as the hero canvas.
     Multi-instance safe (a merchant can add more than one Convergence
     section), same registry-array pattern as the hero.
     ========================================================================== */
  var BATTERY_FRAME_COUNT = 96;
  // The unified/center battery only becomes visible once the outer ones are
  // well converged (see the opacity threshold on .cx-battery--unified in
  // assets/cinemax.css — must stay in sync with this value). If its frame
  // index tracked the same section-wide p, ~60% of its rotation would
  // already have played out invisibly before it appears, showing only the
  // tail end. Remapping its own progress to start at 0 exactly when it
  // starts revealing lets its FULL rotation play out within its visible
  // window.
  var BATTERY_UNIFIED_REVEAL_START = 0.6;
  // Only the first 80% of the pinned track drives p from 0 to 1 — the
  // remaining 20% is a pure "hold" buffer where raw scroll keeps moving but
  // p stays clamped at 1 (already fully settled). Tuning the lerp's
  // catch-up speed to race a fast flick is a losing game (position:sticky
  // releases on raw scroll, instantly, with no regard for JS state);
  // reserving real scroll distance the animation never needed guarantees
  // the pin structurally outlasts the smoothing, however fast the visitor
  // scrolls.
  var CONVERGENCE_ACTIVE_FRACTION = 0.8;

  function drawBatteryFrame(battery, p) {
    battery.wantP = p;
    if (!battery.total) return;
    var fi = Math.round(clamp01(p) * (battery.total - 1));
    if (fi === battery.lastIndex) return;
    var img = battery.frames[fi];
    if (!img || !img.complete || !img.naturalWidth) return;
    battery.lastIndex = fi;
    battery.ctx.clearRect(0, 0, battery.W, battery.H);
    var w = battery.W, h = w / battery.ratio;
    if (h > battery.H) { h = battery.H; w = h * battery.ratio; }
    battery.ctx.drawImage(img, (battery.W - w) / 2, (battery.H - h) / 2, w, h);
  }

  function resizeBattery(battery) {
    var rect = battery.canvas.getBoundingClientRect();
    // 5 canvases redrawing every scroll tick is real GPU work; capping the
    // backing-store resolution lower on small screens (where the extra
    // sharpness is least visible anyway) trades a bit of crispness for
    // smoother scrubbing on weaker mobile hardware.
    var dprCap = window.innerWidth <= 760 ? 1.5 : 2;
    battery.dpr = Math.min(window.devicePixelRatio || 1, dprCap);
    battery.W = rect.width;
    battery.H = rect.height;
    battery.canvas.width = Math.round(battery.W * battery.dpr);
    battery.canvas.height = Math.round(battery.H * battery.dpr);
    battery.ctx.setTransform(battery.dpr, 0, 0, battery.dpr, 0, 0);
    battery.ctx.imageSmoothingEnabled = true;
    battery.ctx.imageSmoothingQuality = 'high';
    battery.lastIndex = -1; // force a redraw at the new size
    drawBatteryFrame(battery, battery.wantP);
  }

  function computeConvergenceTarget(entry) {
    var rect = entry.track.getBoundingClientRect();
    // Same reasoning as computeHeroProgress: measure the actual pinned
    // stage instead of window.innerHeight, so this can't desync from the
    // stable 100svh the CSS uses when mobile browser chrome shows/hides
    // mid-scroll.
    var total = rect.height - entry.stage.getBoundingClientRect().height;
    if (total <= 0) return 0;
    var raw = (-rect.top) / total;
    return clamp01(raw / CONVERGENCE_ACTIVE_FRACTION);
  }

  function tickConvergence(entry) {
    // Single consistent smoothing factor everywhere, including at the 0/1
    // extremes — special-casing an instant snap right at the boundary
    // fixes a lerp/cutoff race but trades it for a worse problem: a visible
    // jump-cut exactly where the eye is most focused. A snappy factor
    // (0.32) keeps the catch-up fast enough in practice — a few frames,
    // ~60-90ms — that it reads as smooth rather than laggy, with no hard
    // discontinuity anywhere; the buffer zone above is what actually
    // guarantees the animation finishes before the pin releases.
    var SMOOTH = prefersReducedMotion ? 1 : 0.32;
    entry.p += (entry.targetP - entry.p) * SMOOTH;
    var settled = Math.abs(entry.targetP - entry.p) < 0.0008;
    if (settled) entry.p = entry.targetP;
    entry.stage.style.setProperty('--p', entry.p.toFixed(4));
    var unifiedP = clamp01((entry.p - BATTERY_UNIFIED_REVEAL_START) / (1 - BATTERY_UNIFIED_REVEAL_START));
    entry.batteries.forEach(function (battery) {
      drawBatteryFrame(battery, battery.key === 'v' ? unifiedP : entry.p);
    });
    if (!settled) {
      entry.ticking = true;
      requestAnimationFrame(function () { tickConvergence(entry); });
    } else {
      entry.ticking = false;
    }
  }

  function pokeConvergence(entry) {
    entry.targetP = computeConvergenceTarget(entry);
    if (!entry.ticking) tickConvergence(entry);
  }

  function initConvergence(section) {
    var track = section.querySelector('[data-cx-conv-track]');
    var stage = section.querySelector('[data-cx-conv-stage]');
    if (!track || !stage) return;

    var dataEl = section.querySelector('[data-cx-battery-frames]');
    var frameData = {};
    try { frameData = JSON.parse(dataEl ? dataEl.textContent : '{}'); } catch (e) { frameData = {}; }

    var entry = { track: track, stage: stage, p: 0, targetP: 0, ticking: false, batteries: [], framesLoaded: false };

    section.querySelectorAll('[data-cx-battery-canvas]').forEach(function (canvas) {
      var key = canvas.getAttribute('data-cx-battery-canvas');
      var urls = frameData[key] || [];
      var battery = {
        canvas: canvas, ctx: canvas.getContext('2d'), key: key, urls: urls,
        frames: new Array(urls.length), total: urls.length, ratio: 720 / 1280,
        W: 0, H: 0, dpr: 1, lastIndex: -1, wantP: 0
      };
      resizeBattery(battery);
      entry.batteries.push(battery);
    });

    function loadFrames() {
      if (entry.framesLoaded) return;
      entry.framesLoaded = true;
      entry.batteries.forEach(function (battery) {
        battery.urls.forEach(function (url, i) {
          var img = new Image();
          img.onload = function () { drawBatteryFrame(battery, battery.wantP); };
          img.src = url;
          battery.frames[i] = img;
        });
      });
    }

    // 5 sequences x 96 frames = 480 images. Fetching all of them at page
    // load — regardless of whether the visitor ever scrolls this far —
    // competed for bandwidth with everything the hero needs for its own
    // first paint, and was the main thing behind the page feeling slow to
    // load. Deferring the actual fetch until the section is getting close
    // (a generous margin, so mobile has time to finish downloading before
    // the pinned scroll actually needs them) fixes that without changing
    // anything about the section itself.
    if (window.IntersectionObserver) {
      var io = new IntersectionObserver(function (ioEntries) {
        ioEntries.forEach(function (ioEntry) {
          if (ioEntry.isIntersecting) { loadFrames(); io.disconnect(); }
        });
      }, { rootMargin: '1500px 0px' });
      io.observe(track);
    } else {
      loadFrames();
    }

    registry.convergence.push(entry);

    if (prefersReducedMotion) {
      entry.p = 1;
      stage.style.setProperty('--p', '1');
      loadFrames();
      entry.batteries.forEach(function (battery) { drawBatteryFrame(battery, 1); });
    }
  }

  /* ---------------- Reveal on scroll ---------------- */
  function initReveals(root) {
    var els = root.querySelectorAll('[data-cx-reveal]');
    els.forEach(function (el) {
      if (registry.reveals.indexOf(el) === -1) registry.reveals.push(el);
    });
    if (prefersReducedMotion) {
      els.forEach(function (el) { el.classList.add('is-visible'); });
    }
  }

  function checkReveals() {
    if (!registry.reveals.length) return;
    var vh = window.innerHeight;
    registry.reveals = registry.reveals.filter(function (el) {
      if (el.getBoundingClientRect().top < vh * 0.86) {
        el.style.transitionDelay = (el.getAttribute('data-cx-reveal-delay') || '0') + 'ms';
        el.classList.add('is-visible');
        return false;
      }
      return true;
    });
  }

  function forceRevealAll() {
    registry.reveals.forEach(function (el) {
      el.style.transitionDelay = '0ms';
      el.classList.add('is-visible');
    });
    registry.reveals = [];
  }

  /* ---------------- Scroll loop ---------------- */
  function onScroll() {
    if (raf) return;
    raf = requestAnimationFrame(function () {
      raf = null;
      checkReveals();
      registry.hero.forEach(pokeHero);
      registry.convergence.forEach(pokeConvergence);
      registry.stickyDock.forEach(pokeStickyDock);
    });
  }

  function bindScrollOnce() {
    if (scrollBound) return;
    scrollBound = true;
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () {
      registry.hero.forEach(resizeHero);
      registry.convergence.forEach(function (entry) {
        entry.batteries.forEach(resizeBattery);
      });
      registry.reviews.forEach(function (entry) {
        measureReviewsLoop(entry);
        if (entry.active) startReviewsLoop(entry);
      });
      registry.stickyDock.forEach(resizeStickyDock);
      onScroll();
    }, { passive: true });
    onScroll();
    setTimeout(checkReveals, 400);
    setTimeout(forceRevealAll, 2400);
  }

  /* ---------------- Before / After comparator ---------------- */
  function initBeforeAfter(section) {
    var comparator = section.querySelector('[data-cx-ba]');
    if (!comparator) return;
    var dragging = false;

    // Autoplay via the HTML attribute alone can silently fail to actually
    // start on some mobile browsers even with muted+playsinline set —
    // nudging it explicitly, and swallowing the rejection if the browser
    // still declines, is the standard defensive fix and costs nothing when
    // autoplay already worked on its own. Gated on the section actually
    // being visible (rather than firing once at page load, which is often
    // before the visitor has scrolled anywhere near it): some mobile
    // browsers are measurably more permissive about starting playback for
    // an element that's actually on screen versus one that's still off
    // below the fold.
    var videos = section.querySelectorAll('.cx-ba__before video, .cx-ba__after video');
    function playVideos() {
      videos.forEach(function (video) {
        var p = video.play();
        if (p && p.catch) p.catch(function () {});
      });
    }
    if (videos.length) {
      if (window.IntersectionObserver) {
        var baIO = new IntersectionObserver(function (ioEntries) {
          ioEntries.forEach(function (ioEntry) {
            if (ioEntry.isIntersecting) playVideos();
          });
        }, { threshold: 0.15 });
        baIO.observe(comparator);
      } else {
        playVideos();
      }
    }

    function setFromClientX(clientX) {
      var r = comparator.getBoundingClientRect();
      var p = (clientX - r.left) / r.width;
      p = clamp01(p);
      comparator.style.setProperty('--sx', (p * 100).toFixed(2) + '%');
      comparator.setAttribute('aria-valuenow', Math.round(p * 100));
    }

    function onDown(e) {
      // Belt-and-suspenders alongside touch-action:none on the comparator
      // (below in cinemax.css): explicitly blocking the default action on
      // the drag gesture itself stops any residual page-scroll/bounce some
      // mobile browsers still attempt right at touch-start, which is what
      // read as the page "jumping back" while dragging the slider.
      e.preventDefault();
      try { comparator.setPointerCapture(e.pointerId); } catch (_) {}
      dragging = true;
      setFromClientX(e.clientX);
    }
    function onMove(e) {
      if (!dragging) return;
      e.preventDefault();
      setFromClientX(e.clientX);
    }
    function onUp() {
      dragging = false;
    }
    function onKey(e) {
      var current = parseFloat(comparator.style.getPropertyValue('--sx')) || 50;
      var step = 5;
      if (e.key === 'ArrowLeft') {
        current = Math.max(0, current - step);
      } else if (e.key === 'ArrowRight') {
        current = Math.min(100, current + step);
      } else {
        return;
      }
      e.preventDefault();
      comparator.style.setProperty('--sx', current + '%');
      comparator.setAttribute('aria-valuenow', Math.round(current));
    }

    comparator.addEventListener('pointerdown', onDown);
    comparator.addEventListener('pointermove', onMove);
    comparator.addEventListener('pointerup', onUp);
    comparator.addEventListener('pointercancel', onUp);
    comparator.addEventListener('keydown', onKey);

    registry.beforeAfter.push({ section: section, comparator: comparator, onDown: onDown, onMove: onMove, onUp: onUp, onKey: onKey });
  }

  /* ---------------- Camera selector ---------------- */
  function initSelector(section) {
    var root = section.querySelector('[data-cx-selector]');
    if (!root) return;

    // Document-wide, not section-scoped: a button in a DIFFERENT section
    // (e.g. the hero's CTA, when its "open camera selector" setting is on)
    // needs to be able to trigger this same modal — Shopify's link picker
    // has no way to target a JS modal directly, so that's the only route.
    var openBtn = document.querySelectorAll('[data-cx-selector-open]');
    var closeEls = root.querySelectorAll('[data-cx-selector-close]');
    var modal = root.querySelector('[data-cx-modal]');
    var brandWrap = root.querySelector('[data-cx-brands]');
    var modelStep = root.querySelector('[data-cx-model-step]');
    var modelWrap = root.querySelector('[data-cx-models]');
    var resultStep = root.querySelector('[data-cx-result]');
    var resultList = root.querySelector('[data-cx-result-list]');
    var otherPanel = root.querySelector('[data-cx-other]');
    var otherCloseEls = root.querySelectorAll('[data-cx-other-close]');

    var dataEl = root.querySelector('[data-cx-camera-data]');
    var cameras = [];
    try { cameras = JSON.parse(dataEl ? dataEl.textContent : '[]'); } catch (e) { cameras = []; }

    var ctaLabel = root.getAttribute('data-cx-cta-label') || '';
    var buyLabel = root.getAttribute('data-cx-buy-label') || '';
    var otherLabel = root.getAttribute('data-cx-other-label') || '';

    var state = { brand: null, model: null };

    function brandsList() {
      var seen = {};
      var out = [];
      cameras.forEach(function (c) {
        if (c.brand && !seen[c.brand]) { seen[c.brand] = true; out.push(c.brand); }
      });
      return out;
    }

    // Several products (Cinemax / Cinemax Pro / Cinemax V2...) can share the
    // same brand+model, so the model step lists each MODEL once (deduped),
    // and the result step below fans back out to every product matching it.
    function modelsForBrand(brand) {
      var seen = {};
      var out = [];
      cameras.forEach(function (c) {
        if (c.brand === brand && c.model && !seen[c.model]) { seen[c.model] = true; out.push(c.model); }
      });
      return out;
    }

    function kitsForBrandModel(brand, model) {
      return cameras.filter(function (c) { return c.brand === brand && c.model === model; });
    }

    function renderBrands() {
      brandWrap.innerHTML = '';
      brandsList().forEach(function (brand) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'cx-chip' + (state.brand === brand ? ' is-active' : '');
        btn.textContent = brand;
        btn.addEventListener('click', function () {
          state.brand = brand;
          state.model = null;
          renderBrands();
          renderModels();
          renderResult();
        });
        brandWrap.appendChild(btn);
      });
    }

    function renderModels() {
      if (!state.brand) {
        modelStep.hidden = true;
        modelWrap.innerHTML = '';
        return;
      }
      modelStep.hidden = false;
      modelWrap.innerHTML = '';
      modelsForBrand(state.brand).forEach(function (model) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'cx-chip' + (state.model === model ? ' is-active' : '');
        btn.textContent = model;
        btn.addEventListener('click', function () {
          state.model = model;
          renderModels();
          renderResult();
        });
        modelWrap.appendChild(btn);
      });

      // "Other" always sits last: not a real camera, opens the small
      // contact-us popup instead of setting state.model.
      var otherBtn = document.createElement('button');
      otherBtn.type = 'button';
      otherBtn.className = 'cx-chip cx-chip--other';
      otherBtn.textContent = otherLabel;
      otherBtn.addEventListener('click', openOther);
      modelWrap.appendChild(otherBtn);
    }

    function renderResult() {
      if (!state.brand || !state.model) {
        resultStep.hidden = true;
        return;
      }
      var kits = kitsForBrandModel(state.brand, state.model);
      if (!kits.length) {
        resultStep.hidden = true;
        return;
      }
      resultStep.hidden = false;
      resultList.innerHTML = '';
      kits.forEach(function (kit) {
        var card = document.createElement('div');
        card.className = 'cx-result__card';

        if (kit.image) {
          var img = document.createElement('img');
          img.className = 'cx-result__img';
          img.src = kit.image;
          img.alt = '';
          img.loading = 'lazy';
          card.appendChild(img);
        }

        var info = document.createElement('div');
        info.className = 'cx-result__info';
        var name = document.createElement('div');
        name.className = 'cx-result__name';
        name.textContent = kit.kitName;
        var price = document.createElement('div');
        price.className = 'cx-result__price';
        price.textContent = kit.price;
        info.appendChild(name);
        info.appendChild(price);
        card.appendChild(info);

        var actions = document.createElement('div');
        actions.className = 'cx-result__actions';

        var viewBtn = document.createElement('a');
        viewBtn.className = 'cx-btn cx-btn--ghost cx-result__cta';
        viewBtn.textContent = ctaLabel + ' ' + kit.kitName;
        if (kit.url) viewBtn.setAttribute('href', kit.url);
        // Dynamically created, so it isn't part of the static closeEls
        // collection wired up below — attach directly instead.
        viewBtn.addEventListener('click', close);
        actions.appendChild(viewBtn);

        if (kit.variantId) {
          var buyBtn = document.createElement('a');
          buyBtn.className = 'cx-btn cx-result__buy';
          buyBtn.textContent = buyLabel;
          buyBtn.setAttribute('href', '/cart/' + kit.variantId + ':1');
          actions.appendChild(buyBtn);
        }

        card.appendChild(actions);
        resultList.appendChild(card);
      });
    }

    function open(e) {
      // preventDefault so a data-cx-selector-open button that's really an
      // <a href="..."> (e.g. a merchant's own homepage button, not one of
      // this section's own <button type="button"> triggers) opens the
      // modal instead of also navigating away.
      if (e) e.preventDefault();
      modal.hidden = false;
      document.body.style.overflow = 'hidden';
      var firstChip = brandWrap.querySelector('.cx-chip');
      if (firstChip) firstChip.focus();
    }
    function close() {
      modal.hidden = true;
      document.body.style.overflow = '';
    }
    function openOther() {
      otherPanel.hidden = false;
    }
    function closeOther() {
      otherPanel.hidden = true;
    }
    function onKeydown(e) {
      if (e.key !== 'Escape') return;
      if (otherPanel && !otherPanel.hidden) { closeOther(); return; }
      if (!modal.hidden) close();
    }

    openBtn.forEach(function (btn) { btn.addEventListener('click', open); });
    closeEls.forEach(function (el) { el.addEventListener('click', close); });
    otherCloseEls.forEach(function (el) { el.addEventListener('click', closeOther); });
    document.addEventListener('keydown', onKeydown);

    renderBrands();
    renderModels();
    renderResult();

    registry.selectors.push({ section: section, open: open, close: close, onKeydown: onKeydown });
  }

  /* ---------------- Sticky bar: lands in its own section, above the footer ----------------
     Stays position:fixed the whole time (avoids the containing-block
     guesswork of switching to position:absolute against whatever ancestor
     the theme happens to give us) — instead, bottom is pushed up to track
     whichever of two things is closer: .cx-sticky-spacer (an empty div
     reserving the bar's own natural spot in this section's normal flow) or
     <footer>. Below both, bottom stays 0 (glued to the viewport, the
     ordinary floating behavior for the rest of the page). Once the spacer's
     natural position rises to meet it, the bar "lands" there and moves 1:1
     with the page from then on instead of continuing to float over its own
     section — which, since that section sits immediately before the
     footer, also means it's already flush above it by construction; the
     footer check stays only as a safety net in case that page order ever
     changes. */
  function initStickyDock(section) {
    var bar = section.querySelector('.cx-sticky');
    var spacer = section.querySelector('.cx-sticky-spacer');
    if (!bar || !spacer) return;
    var entry = { bar: bar, spacer: spacer, footer: document.querySelector('footer') };
    resizeStickyDock(entry);
    registry.stickyDock.push(entry);
  }

  function resizeStickyDock(entry) {
    // .cx-sticky-spacer's CSS height was a hardcoded guess at the bar's
    // height; the bar's real height is content-driven (font sizes,
    // padding) and had since grown past it. That gap between "space
    // reserved in normal flow" and "space the landed bar actually needs"
    // is what showed up as a blank strip right before the footer. Syncing
    // it to the bar's real, measured height removes the gap regardless of
    // how the bar's own styling changes in the future.
    entry.spacer.style.height = entry.bar.offsetHeight + 'px';
  }

  function pokeStickyDock(entry) {
    var barHeight = entry.bar.offsetHeight;
    var landed = Math.max(0, window.innerHeight - entry.spacer.getBoundingClientRect().top - barHeight);
    var footerVisible = 0;
    if (entry.footer) {
      var footerRect = entry.footer.getBoundingClientRect();
      footerVisible = Math.max(0, Math.min(footerRect.height, window.innerHeight - footerRect.top));
    }
    entry.bar.style.bottom = Math.max(landed, footerVisible) + 'px';
  }

  /* ---------------- Reviews: infinite marquee ----------------
     Only engages on desktop (pointer:fine, >=900px) and only when the
     cards are actually wider than the viewport — otherwise the section is
     left as a plain, centered, wrapping row of the real cards (its CSS
     default, no JS needed). The Liquid renders the cards twice, flat,
     side by side in one row (the real set immediately followed by an
     aria-hidden duplicate set); measuring the gap between the first real
     card and the first duplicate gives an exact loop distance, so the loop
     wraps seamlessly without any runtime DOM cloning.
     Motion model: idle drifts left forever, at a constant slow speed.
     Freezes the instant the pointer enters a card. While frozen, a wheel
     event is captured (preventDefault, so the page itself doesn't also
     scroll) and its delta drives the target offset — "scrolling" the
     cards instead of the page. Both the idle drift and the wheel input
     only ever move a *target*; the actually-rendered offset eases toward
     it every frame, which is what makes wheel input (naturally chunky,
     especially from a physical mouse wheel) read as fluid instead of
     jumping in discrete steps. */
  var REVIEWS_DRIFT_PX_PER_MS = 0.035;
  var REVIEWS_WHEEL_SMOOTH = 0.18;

  function measureReviewsLoop(entry) {
    var cards = entry.track.querySelectorAll('.cx-review:not([data-cx-reviews-dup])');
    var dups = entry.track.querySelectorAll('.cx-review[data-cx-reviews-dup]');
    if (!cards.length || !dups.length) { entry.active = false; return; }

    var gap = parseFloat(window.getComputedStyle(entry.track).columnGap) || 0;
    var naturalWidth = 0;
    for (var i = 0; i < cards.length; i++) {
      naturalWidth += cards[i].offsetWidth;
      if (i > 0) naturalWidth += gap;
    }

    var isDesktop = window.matchMedia('(min-width:900px) and (pointer:fine)').matches;
    entry.active = isDesktop && !prefersReducedMotion && naturalWidth > entry.viewport.clientWidth;

    if (entry.active) {
      entry.reviewsEl.setAttribute('data-cx-marquee', '');
      var r1 = cards[0].getBoundingClientRect();
      var r2 = dups[0].getBoundingClientRect();
      entry.loopWidth = r2.left - r1.left;
    } else {
      entry.reviewsEl.removeAttribute('data-cx-marquee');
      entry.track.style.transform = '';
      entry.offset = 0;
      entry.targetOffset = 0;
      entry.loopWidth = 0;
    }
  }

  function tickReviews(entry) {
    if (!entry.active) { entry.ticking = false; return; }
    var now = performance.now();
    var dt = entry.lastTime ? Math.min(now - entry.lastTime, 100) : 16;
    entry.lastTime = now;
    if (!entry.hovered) entry.targetOffset -= REVIEWS_DRIFT_PX_PER_MS * dt;
    entry.offset += (entry.targetOffset - entry.offset) * REVIEWS_WHEEL_SMOOTH;
    if (entry.loopWidth > 0) {
      // Shift target AND offset together so the loop wraps without the
      // eased value snapping/rubber-banding back toward the new target.
      while (entry.targetOffset <= -entry.loopWidth) { entry.targetOffset += entry.loopWidth; entry.offset += entry.loopWidth; }
      while (entry.targetOffset > 0) { entry.targetOffset -= entry.loopWidth; entry.offset -= entry.loopWidth; }
    }
    entry.track.style.transform = 'translateX(' + entry.offset.toFixed(2) + 'px)';
    requestAnimationFrame(function () { tickReviews(entry); });
  }

  function startReviewsLoop(entry) {
    if (entry.ticking) return;
    entry.ticking = true;
    entry.lastTime = 0;
    requestAnimationFrame(function () { tickReviews(entry); });
  }

  function initReviews(section) {
    var reviewsEl = section.querySelector('.cx-reviews');
    var viewport = section.querySelector('[data-cx-reviews-viewport]');
    var track = section.querySelector('[data-cx-reviews-track]');
    if (!reviewsEl || !viewport || !track) return;

    var entry = {
      reviewsEl: reviewsEl, viewport: viewport, track: track,
      offset: 0, targetOffset: 0, loopWidth: 0, active: false, hovered: false,
      lastTime: 0, ticking: false
    };

    function onEnter() { entry.hovered = true; }
    function onLeave() { entry.hovered = false; }
    function onWheel(e) {
      if (!entry.active || !entry.hovered) return;
      e.preventDefault();
      entry.targetOffset -= e.deltaY;
    }

    viewport.addEventListener('mouseenter', onEnter);
    viewport.addEventListener('mouseleave', onLeave);
    viewport.addEventListener('wheel', onWheel, { passive: false });
    entry.onEnter = onEnter; entry.onLeave = onLeave; entry.onWheel = onWheel;

    measureReviewsLoop(entry);
    registry.reviews.push(entry);
    if (entry.active) startReviewsLoop(entry);
  }

  /* ---------------- Newsletter (progressive enhancement only) ---------------- */
  function initNewsletter(section) {
    var form = section.querySelector('[data-cx-newsletter-form]');
    if (!form) return;
    // Native Shopify {% form 'customer' %} handles submission; nothing to wire.
  }

  /* ---------------- Section lifecycle ---------------- */
  function teardownSection(section) {
    registry.hero = registry.hero.filter(function (e) { return !section.contains(e.track); });
    registry.convergence = registry.convergence.filter(function (e) { return !section.contains(e.track); });
    registry.reveals = registry.reveals.filter(function (el) { return !section.contains(el); });
    registry.beforeAfter = registry.beforeAfter.filter(function (e) {
      if (section.contains(e.section)) {
        e.comparator.removeEventListener('pointerdown', e.onDown);
        e.comparator.removeEventListener('pointermove', e.onMove);
        e.comparator.removeEventListener('pointerup', e.onUp);
        e.comparator.removeEventListener('pointercancel', e.onUp);
        e.comparator.removeEventListener('keydown', e.onKey);
        return false;
      }
      return true;
    });
    registry.selectors = registry.selectors.filter(function (e) {
      if (section.contains(e.section)) {
        document.removeEventListener('keydown', e.onKeydown);
        return false;
      }
      return true;
    });
    registry.stickyDock = registry.stickyDock.filter(function (e) { return !section.contains(e.bar); });
    registry.reviews = registry.reviews.filter(function (e) {
      if (section.contains(e.viewport)) {
        e.viewport.removeEventListener('mouseenter', e.onEnter);
        e.viewport.removeEventListener('mouseleave', e.onLeave);
        e.viewport.removeEventListener('wheel', e.onWheel);
        e.active = false; // lets the in-flight rAF chain, if any, stop itself
        return false;
      }
      return true;
    });
  }

  function initSection(section) {
    if (section.hasAttribute('data-cx-hero-section')) initHero(section);
    if (section.hasAttribute('data-cx-conv-section')) initConvergence(section);
    if (section.hasAttribute('data-cx-ba-section')) initBeforeAfter(section);
    if (section.hasAttribute('data-cx-selector-section')) { initSelector(section); initStickyDock(section); }
    if (section.hasAttribute('data-cx-newsletter-section')) initNewsletter(section);
    if (section.hasAttribute('data-cx-reviews-section')) initReviews(section);
    initReveals(section);
    bindScrollOnce();
    onScroll();
  }

  function initAll() {
    document.querySelectorAll('.cinemax-lp[data-cx-section]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector('.cinemax-lp[data-cx-section]');
    if (section) initSection(section);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var section = e.target.querySelector('.cinemax-lp[data-cx-section]');
    if (section) teardownSection(section);
  });
})();
